import { BrandLogo } from "@/components/BrandLogo";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Youtube, Facebook, Phone, Mail } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const SOCIAL = [
  { icon: Instagram, key: "instagram" },
  { icon: Facebook, key: "facebook" },
  { icon: Twitter, key: "twitter" },
  { icon: Youtube, key: "youtube" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

const LinkItem = ({ l, t }) => {
  const cls = "hover:text-[#d00000] transition-colors text-sm";
  const id = `footer-link-${slug(l.label)}`;
  return /^https?:/.test(l.to)
    ? <a href={l.to} target="_blank" rel="noreferrer" className={cls} data-testid={id}>{t(l.label)}</a>
    : <Link to={l.to} className={cls} data-testid={id}>{t(l.label)}</Link>;
};

const Col = ({ title, links, t }) => (
  <div>
    <p className="text-xs uppercase tracking-[0.2em] text-neutral-400 mb-4">{t(title)}</p>
    <ul className="space-y-3 text-neutral-600">
      {(links || []).map((l) => <li key={l.label + l.to}><LinkItem l={l} t={t} /></li>)}
    </ul>
  </div>
);

export const Footer = () => {
  const { config, t } = useSite();
  const f = config?.footer;
  const g = config?.general;
  if (!f || !g) return <footer className="mt-24 h-64 bg-white border-t border-neutral-200" data-testid="footer" />;
  const socials = SOCIAL.filter((s) => f[s.key]);
  return (
    <footer className="bg-white border-t border-neutral-200 mt-24" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-10">
          <div className="lg:col-span-2">
            <div className="mb-3"><BrandLogo className="h-14" testId="footer-logo-link" /></div>
            <p className="font-display italic text-lg text-[#d00000] mb-4">{t(g.tagline)}</p>
            <p className="text-neutral-600 max-w-sm leading-relaxed text-sm">{t(f.description)}</p>
            <div className="mt-6 space-y-2 text-sm">
              {g.contact_phone && <a href={`tel:${g.contact_phone}`} className="flex items-center gap-2 text-neutral-700 hover:text-[#d00000] transition-colors" data-testid="footer-phone"><Phone className="w-4 h-4 text-[#d00000]" /> {g.contact_phone}</a>}
              {g.contact_email && <a href={`mailto:${g.contact_email}`} className="flex items-center gap-2 text-neutral-700 hover:text-[#d00000] transition-colors" data-testid="footer-email-admin"><Mail className="w-4 h-4 text-[#d00000]" /> {g.contact_email}</a>}
              {g.support_email && <a href={`mailto:${g.support_email}`} className="flex items-center gap-2 text-neutral-700 hover:text-[#d00000] transition-colors" data-testid="footer-email-support"><Mail className="w-4 h-4 text-[#d00000]" /> {g.support_email}</a>}
            </div>
            {socials.length > 0 && (
              <div className="flex gap-3 mt-6">
                {socials.map((s) => (
                  <a key={s.key} href={f[s.key]} target="_blank" rel="noreferrer" aria-label={s.key} data-testid={`footer-social-${s.key}`}
                    className="w-10 h-10 border border-neutral-300 flex items-center justify-center text-neutral-500 hover:text-white hover:bg-[#d00000] hover:border-[#d00000] transition-colors">
                    <s.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>
          {(f.columns || []).slice(0, 4).map((c) => <Col key={c.title} title={c.title} links={c.links} t={t} />)}
        </div>
      </div>
      <div className="border-t border-neutral-200 py-6 text-center text-neutral-400 text-sm" data-testid="footer-copyright">
        © {new Date().getFullYear()} {t(f.copyright)}
      </div>
    </footer>
  );
};
